import { createContext, useContext, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartId, setCartId] = useState(null);

  const saveCartItem = async (item) => {
    try {
      const guestToken = localStorage.getItem("guest_token");
      // console.log("Guest Token:", guestToken); 

      if (!guestToken) {
        console.error("Error: Guest token is missing!");
        return null; // Stop API call if no token
      }

      // Prepare the payload
      const payload = {
        product_id: item.id, // Ensure this is a valid number
        quantity: item.quantity || 1, // Use the item's quantity or default to 1
        size_id: item.size_id, // Ensure this is a valid number
      };

      // console.log("Sending request with:", payload);

      const response = await axios.post(
        "https://admin.utharamenon.com/api/guest-cart/save",
        payload,
        {
          headers: {
            guest_token: `${guestToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        // console.log("Response received:", response.data);

        // Store the cart_id from the response
        setCartId(response.data.cart_id);
      }

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error("Error status:", error.response.status);
        console.error("Error data:", error.response.data);
      } else {
        console.error("Error:", error.message);
      }
      return null;
    }
  };

  // Function to remove cart item from backend using DELETE
  const removeCartItem = async (id, size) => {
    try {
      const guestToken = localStorage.getItem("guest_token");

      const response = await axios.delete(
        "https://admin.utharamenon.com/api/guest-cart/remove",
        {
          headers: {
            guest_token: `${guestToken}`,
          },
          data: {
            product_id: id,
            size: size,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error removing cart item:", error);
      return null;
    }
  };

  // Add item to cart or increase quantity if it already exists
  const addToCart = async (item) => {
    setCartItems((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (cartItem) => cartItem.id === item.id && cartItem.size === item.size
      );

      if (existingItemIndex !== -1) {
        // If the item already exists, increment its quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += 1;
        return updatedCart;
      } else {
        // If the item doesn't exist, add it to the cart with a quantity of 1
        return [...prevCart, { ...item, quantity: 1 }];
      }
    });

    // Save item to the backend
    await saveCartItem(item);
  };

  // Remove item from cart (Frontend + API Call)
  const removeFromCart = async (id, size) => {
    const success = await removeCartItem(id, size);

    if (success) {
      setCartItems((prevCart) =>
        prevCart.filter((item) => !(item.id === id && item.size === size))
      );
    }
  };

  // Increase quantity of an item in the cart
  const increaseQuantity = (id, size) => {
    setCartItems((prevCart) =>
      prevCart.map((item) => {
        if (item.id === id && item.size === size) {
          // console.log("Stock:", item.stock, "Current Quantity:", item.quantity); 

          if (item.quantity < item.stock) {
            // ✅ Allow increasing until stock is reached
            return { ...item, quantity: item.quantity + 1 };
          } else {
            // toast.error("Stock limit reached! You can't add more.", { className: "toast-error" });
            return item; 
          }
        }
        return item;
      })
    );
  };

  // Decrease quantity of an item in the cart
  const decreaseQuantity = (id, size) => {
    setCartItems((prevCart) =>
      prevCart.map((item) =>
        item.id === id && item.size === size && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };
  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartId,
        addToCart,
        removeFromCart,
        increaseQuantity,
        decreaseQuantity,
        clearCart
      }}
    >
      {children}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeButton={false} />
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};